import { inject, Injectable } from '@angular/core';
import { Factory } from '../factory';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IBannerForm, IBannerFormData } from '../../entities/banner-entity';
import { IAccessibleImageForm } from '../../entities/image-entity';
import { FilterTextsPipe } from '../../pipes/filterTexts/filterTexts.pipe';
import { DEFAULT_BANNER, IBanner, ERelationType } from '@web/dtos';

@Injectable({
  providedIn: 'root',
})
export class BannerFormsFactoryService extends Factory<IBannerForm, IBanner> {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  filterTexts: FilterTextsPipe = inject(FilterTextsPipe);

  buildForm(banner: IBanner = DEFAULT_BANNER): FormGroup<IBannerForm> {
    return this.formBuilder.group<IBannerForm>({
      id: this.formBuilder.control(banner.id || null),

      isImageActive: this.formBuilder.nonNullable.control(
        banner.isImageActive,
        [Validators.required]
      ),
      isTextActive: this.formBuilder.nonNullable.control(
        banner.isTextActive,
        Validators.required
      ),
      accessibleImage: this.formBuilder.group<IAccessibleImageForm>({
        id: this.formBuilder.control(banner.accessibleImage.id || null),
        image: this.formBuilder.control(banner.accessibleImage.image, [
          Validators.required,
        ]),
        imageAlts: this.formBuilder.nonNullable.control(
          banner.accessibleImage.imageAlts,
          [Validators.required]
        ),
        url: this.formBuilder.control(banner.accessibleImage.url || null),
        positionOrder: this.formBuilder.control(
          banner.accessibleImage.positionOrder,
          []
        ),
      }),
      imageHeight: this.formBuilder.nonNullable.control(
        banner.imageHeight,
        Validators.required
      ),
      headlines: this.formBuilder.nonNullable.control(
        this.filterTexts.transform(banner.texts, ERelationType.headline),
        [Validators.required]
      ),
      headlineFontSize: this.formBuilder.nonNullable.control<number>(
        banner.headlineFontSize,
        [Validators.required]
      ),
      subHeadlines: this.formBuilder.nonNullable.control(
        this.filterTexts.transform(banner.texts, ERelationType.subHeadline),
        [Validators.required]
      ),
      subHeadlineFontSize: this.formBuilder.nonNullable.control<number>(
        banner.subHeadlineFontSize,
        [Validators.required]
      ),
      textAlignmentHorizontal: this.formBuilder.nonNullable.control(
        banner.textAlignmentHorizontal,
        [Validators.required]
      ),
      textAlignmentVertical: this.formBuilder.nonNullable.control(
        banner.textAlignmentVertical,
        [Validators.required]
      ),
      textFont: this.formBuilder.control(banner.textFont, [
        Validators.required,
      ]),
      textColor: this.formBuilder.nonNullable.control(banner.textColor, [
        Validators.required,
      ]),
    });
  }

  buildEntity(form: FormGroup<IBannerForm>): IBanner {
    const banner = form.getRawValue() as IBannerFormData;

    const newBanner: any = {
      ...banner,
      texts: [...banner.headlines, ...banner.subHeadlines],
    };

    delete newBanner.headlines;
    delete newBanner.subHeadlines;

    this.removeNullIds(newBanner);
    return newBanner as IBanner;
  }
}
